import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ApiActiveService } from '../data/services/api/api-active.service';
import { ActivatedRoute } from '@angular/router';
import { Asset, Provider, User } from '../interfaces/asset.interface';

// ===== TYPE GUARDS =====
function isProvider(obj: any): obj is Provider {
  return obj && typeof obj === 'object' && 'fullName' in obj && 'rfc' in obj;
}

function isUser(obj: any): obj is User {
  return obj && typeof obj === 'object' && 'names' in obj && 'lastNames' in obj;
}

@Component({
  selector: 'app-active-detail',
  templateUrl: './active-detail.component.html',
  styleUrls: ['./active-detail.component.scss']
})
export class ActiveDetailComponent {
  id!: string;
  asset!: Asset;
  loading: boolean = true;
  editLoading: boolean = false;

  // Modal properties
  showEditModal: boolean = false;
  editForm!: FormGroup;
  modalLoading: boolean = false;
  
  // Providers data
  providers: { label: string; value: string; }[] = [];

  constructor(
    private apiActiveService: ApiActiveService,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    this.id = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    this.getAssetData();
  }

  getAssetData() {
    this.loading = true;
    this.apiActiveService.getById(this.id).subscribe((response) => {
      if (!response.error) {
        this.asset = response.data;
        
        // Asegurar que delivery existe con estructura por defecto
        if (!this.asset.delivery) {
          this.asset.delivery = { date: null, proof: '' };
        }
      } else {
        console.error(response.msg);
      }
      this.loading = false;
    });
  }

  editAsset() {
    if (!this.asset) {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'No se encontraron datos del activo para editar' 
      });
      return;
    }

    this.loadProviders(); // Cargar proveedores cuando se abre el modal
    this.initializeEditForm();
    this.showEditModal = true;
  }

  initializeEditForm() {
    // Crear formulario expandido con todos los campos editables
    this.editForm = this.fb.group({
      // Información Básica
      name: [this.asset.name || '', [Validators.required, Validators.minLength(3)]],
      category: [this.asset.category || '', [Validators.required]],
      brand: [this.asset.brand || '', [Validators.required]],
      assetModel: [this.asset.assetModel || '', [Validators.required]],
      version: [this.asset.version || '', [Validators.required]],
      color: [this.asset.color || '', [Validators.required, this.onlyLettersValidator]],
      description: [this.asset.description || '', [Validators.required]],
      
      // Especificaciones Técnicas
      serie: [this.asset.serie || '', [Validators.required]],
      engineNumber: [this.asset.engineNumber || ''],
      
      // Información Financiera - Adquisición
      acquisition: this.fb.group({
        amount: [this.asset.acquisition?.amount || '', [Validators.required, this.nonNegativeValidator]],
        iva: [this.asset.acquisition?.iva || '', [Validators.required, this.nonNegativeValidator]],
        total: [{ value: this.asset.acquisition?.total || '', disabled: true }], // Calculado automáticamente
        date: [this.asset.acquisition?.date || ''], // Sin validación requerida
        vrn: [this.asset.acquisition?.vrn || ''], // Placas/VRN (opcional)
        // Campos de archivos
        paymentProof: [this.asset.acquisition?.paymentProof || ''],
        invoicePdf: [this.asset.acquisition?.invoicePdf || ''],
        invoiceXml: [this.asset.acquisition?.invoiceXml || ''],
        circulationCard: [this.asset.acquisition?.circulationCard || ''],
        permisson: [this.asset.acquisition?.permisson || ''],
        inssurancePolice: [this.asset.acquisition?.inssurancePolice || ''],
        gps: [this.asset.acquisition?.gps || '']
      }),
      
      // Información Financiera - Valor de Venta (Opcional)
      saleValue: this.fb.group({
        amount: [this.asset.saleValue?.amount || ''],
        iva: [this.asset.saleValue?.iva || ''],
        total: [{ value: this.asset.saleValue?.total || '', disabled: true }] // Calculado automáticamente
      }),
      
      // Fechas - Delivery (coincide con backend)
      delivery: this.fb.group({
        date: [this.asset.delivery?.date || ''],
        proof: [this.asset.delivery?.proof || '']
      }),
      
      // Estado y Configuración
      status: [this.asset.status || 'Activo', [Validators.required]],
      provider: [
        typeof this.asset.provider === 'string' 
          ? this.asset.provider 
          : this.asset.provider?._id || '', 
        [Validators.required]
      ]
    });

    // Configurar cálculos automáticos
    this.setupAutomaticCalculations();
    
    // Calcular totales iniciales
    this.calculateAcquisitionTotal();
    this.calculateSaleValueTotal();
  }

  private setupAutomaticCalculations() {
    // Cálculo automático para acquisition.total
    const acquisitionGroup = this.editForm.get('acquisition') as FormGroup;
    if (acquisitionGroup) {
      const amountControl = acquisitionGroup.get('amount');
      const ivaControl = acquisitionGroup.get('iva');
      const totalControl = acquisitionGroup.get('total');

      if (amountControl && ivaControl && totalControl) {
        // Suscribirse a cambios en amount
        amountControl.valueChanges.subscribe(() => {
          this.calculateAcquisitionTotal();
        });

        // Suscribirse a cambios en iva
        ivaControl.valueChanges.subscribe(() => {
          this.calculateAcquisitionTotal();
        });
      }
    }

    // Cálculo automático para saleValue.total
    const saleValueGroup = this.editForm.get('saleValue') as FormGroup;
    if (saleValueGroup) {
      const amountControl = saleValueGroup.get('amount');
      const ivaControl = saleValueGroup.get('iva');
      const totalControl = saleValueGroup.get('total');

      if (amountControl && ivaControl && totalControl) {
        // Suscribirse a cambios en amount
        amountControl.valueChanges.subscribe(() => {
          this.calculateSaleValueTotal();
        });

        // Suscribirse a cambios en iva
        ivaControl.valueChanges.subscribe(() => {
          this.calculateSaleValueTotal();
        });
      }
    }
  }

  private calculateAcquisitionTotal() {
    const acquisitionGroup = this.editForm.get('acquisition') as FormGroup;
    if (acquisitionGroup) {
      const amount = acquisitionGroup.get('amount')?.value || 0;
      const iva = acquisitionGroup.get('iva')?.value || 0;
      const total = Number(amount) + Number(iva);
      
      acquisitionGroup.get('total')?.setValue(total);
    }
  }

  private calculateSaleValueTotal() {
    const saleValueGroup = this.editForm.get('saleValue') as FormGroup;
    if (saleValueGroup) {
      const amount = saleValueGroup.get('amount')?.value || 0;
      const iva = saleValueGroup.get('iva')?.value || 0;
      const total = Number(amount) + Number(iva);
      
      saleValueGroup.get('total')?.setValue(total);
    }
  }

  // Validadores locales
  private onlyLettersValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const valid = /^[a-zA-Z\s]*$/.test(value);
    if (!valid) return { onlyLetters: 'Solo se permiten letras' };
    return null;
  }

  private nonNegativeValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value < 0) return { negativeValue: 'El valor no puede ser negativo' };
    return null;
  }

  // Función para sanitizar los datos antes del envío
  private sanitizeAssetData(formData: any): Partial<Asset> {
    console.log('FormData recibido:', formData); // Para debug
    
    const sanitizedData: any = {
      name: formData.name,
      category: formData.category,
      brand: formData.brand,
      assetModel: formData.assetModel,
      version: formData.version,
      color: formData.color,
      description: formData.description,
      serie: formData.serie,
      engineNumber: formData.engineNumber,
      status: formData.status,
      provider: formData.provider,
      acquisition: {
        amount: Number(formData.acquisition?.amount) || 0,
        iva: Number(formData.acquisition?.iva) || 0,
        total: Number(formData.acquisition?.total) || 0,
        date: formData.acquisition?.date || null,
        vrn: formData.acquisition?.vrn || '',
        // Campos de archivos
        paymentProof: formData.acquisition?.paymentProof || '',
        invoicePdf: formData.acquisition?.invoicePdf || '',
        invoiceXml: formData.acquisition?.invoiceXml || '',
        circulationCard: formData.acquisition?.circulationCard || '',
        permisson: formData.acquisition?.permisson || '',
        inssurancePolice: formData.acquisition?.inssurancePolice || '',
        gps: formData.acquisition?.gps || ''
      },
      delivery: {
        date: formData.delivery?.date || null,
        proof: formData.delivery?.proof || ''
      }
    };

    // Solo incluir saleValue si tiene datos
    if (formData.saleValue?.amount || formData.saleValue?.iva || formData.saleValue?.total) {
      sanitizedData.saleValue = {
        amount: Number(formData.saleValue.amount) || 0,
        iva: Number(formData.saleValue.iva) || 0,
        total: Number(formData.saleValue.total) || 0
      };
    }

    console.log('Datos sanitizados:', sanitizedData); // Para debug

    return sanitizedData;
  }

  saveAssetChanges() {
    if (!this.editForm.valid) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atención', 
        detail: 'Por favor complete todos los campos requeridos correctamente' 
      });
      return;
    }

    this.modalLoading = true;
    
    // Obtener datos del formulario incluyendo campos deshabilitados
    const formData = this.editForm.getRawValue(); // getRawValue() incluye campos deshabilitados
    
    // Sanitizar datos antes del envío
    const updateData = this.sanitizeAssetData(formData);

    console.log('Datos a enviar:', updateData); // Para debug

    this.apiActiveService.update(this.id, updateData).subscribe({
      next: (response) => {
        this.modalLoading = false;
        if (!response.error) {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Éxito', 
            detail: 'Información del activo actualizada correctamente' 
          });
          
          // Preservar el objeto Provider original si solo se está actualizando el ID
          const updatedAsset = { ...this.asset, ...formData };
          
          // Si formData.provider es un string ID y this.asset.provider es un objeto,
          // preservar el objeto Provider original
          if (typeof formData.provider === 'string' && 
              this.asset.provider && 
              typeof this.asset.provider === 'object') {
            updatedAsset.provider = this.asset.provider;
          }

          // Actualizar datos locales
          this.asset = updatedAsset;
          this.asset.acquisition = formData.acquisition;
          this.asset.saleValue = formData.saleValue;
          this.asset.delivery = formData.delivery;
          
          this.closeEditModal();
        } else {
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: response.msg || 'Error al actualizar el activo' 
          });
        }
      },
      error: (error) => {
        this.modalLoading = false;
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Error de conexión al actualizar el activo' 
        });
        console.error('Error updating asset:', error);
      }
    });
  }

  closeEditModal() {
    this.showEditModal = false;
    this.modalLoading = false;
    this.editForm?.reset();
  }

  onModalHide() {
    this.closeEditModal();
  }

  // ===== FILE MANAGEMENT METHODS =====
  
  /**
   * Manejar cuando se sube un archivo
   */
  onFileSelected(fileUrl: string, documentType: string, section: 'acquisition' | 'delivery'): void {
    if (!this.editForm) return;

    const sectionGroup = this.editForm.get(section);
    if (sectionGroup) {
      sectionGroup.patchValue({
        [documentType]: fileUrl
      });
    }
  }

  /**
   * Manejar cuando se elimina un archivo
   */
  onFileDeleted(documentType: string, section: 'acquisition' | 'delivery'): void {
    if (!this.editForm) return;

    const sectionGroup = this.editForm.get(section);
    if (sectionGroup) {
      sectionGroup.patchValue({
        [documentType]: ''
      });
    }
  }

  /**
   * Manejar descarga de archivos
   */
  onDownloadRequested(fileUrl: string, fileName: string): void {
    if (!fileUrl) return;

    this.apiActiveService.downloadDocumentByUrl(fileUrl).subscribe({
      next: (blob) => {
        if (!blob || blob.size === 0) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'El archivo descargado está vacío. Intente de nuevo.'
          });
          return;
        }

        const extension = fileUrl.includes('.') ? fileUrl.substring(fileUrl.lastIndexOf('.')) : '';
        const downloadName = fileName + (!fileName.includes('.') && extension ? extension : '');

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Descarga',
          detail: 'Archivo descargado correctamente.'
        });
      },
      error: (error) => {
        console.error('Error al descargar archivo:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al descargar el archivo.'
        });
      }
    });
  }

  /**
   * Descargar documento desde las cards de documentación
   */
  downloadDocument(documentUrl: string, documentType: string): void {
    if (!documentUrl) return;
    
    const fileNameMap: { [key: string]: string } = {
      'payment-proof': 'comprobante_pago',
      'delivery-proof': 'comprobante_entrega',
      'invoice-pdf': 'factura_pdf', 
      'invoice-xml': 'factura_xml',
      'circulation-card': 'tarjeta_circulacion',
      'insurance-police': 'poliza_seguro',
      'gps': 'documento_gps',
      'permisson': 'permisos'
    };
    
    const fileName = fileNameMap[documentType] || documentType;
    this.onDownloadRequested(documentUrl, fileName);
  }

  // ===== TYPE GUARD HELPERS =====
  isProvider(obj: any): obj is Provider {
    return isProvider(obj);
  }

  isUser(obj: any): obj is User {
    return isUser(obj);
  }

  // Helpers para acceso seguro en template
  getProviderName(): string {
    if (this.isProvider(this.asset?.provider)) {
      return this.asset.provider.fullName;
    }
    return typeof this.asset?.provider === 'string' ? this.asset.provider : 'N/A';
  }

  getBankerName(): string {
    if (this.isUser(this.asset?.banker)) {
      return `${this.asset.banker.names} ${this.asset.banker.lastNames}`;
    }
    return typeof this.asset?.banker === 'string' ? this.asset.banker : 'N/A';
  }

  // Método para cargar proveedores
  loadProviders() {
    this.apiActiveService.fetchProviders({ page: 1, limit: 1000, filters: {} }).subscribe(
      (response) => {
        this.providers = response;
      },
      (error) => {
        console.error('Error al obtener los proveedores', error);
        this.providers = [];
      }
    );
  }
}